import { supabase } from './supabase';
import { err, ok, type Result } from './result';

export interface UserProfile {
  userId: string;
  fullName: string;
  timezone: string;
  persona?: string | null;
  avatarUrl?: string | null;
  bio?: string | null;
}

export interface ProfileSummary {
  fullName: string | null;
  persona: string | null;
  avatarUrl: string | null;
}

const mapProfileRow = (row: any): UserProfile => ({
  userId: row.user_id,
  fullName: row.full_name ?? '',
  timezone: row.timezone ?? 'UTC',
  persona: row.persona ?? null,
  avatarUrl: row.avatar_url ?? null,
  bio: row.bio ?? null,
});

export const fetchCurrentProfile = async (): Promise<UserProfile | null> => {
  const {
    data: { user },
    error: authError,
  } = await supabase.auth.getUser();
  if (authError) throw authError;
  if (!user) return null;

  const { data, error } = await supabase
    .from('profiles')
    .select('user_id, full_name, persona, timezone, avatar_url, bio')
    .eq('user_id', user.id)
    .maybeSingle();
  if (error) throw error;
  return data ? mapProfileRow(data) : null;
};

interface UpsertProfileInput {
  fullName: string;
  timezone: string;
  persona?: string;
  bio?: string;
  avatarUrl?: string;
}

export const upsertCurrentProfile = async (input: UpsertProfileInput): Promise<void> => {
  const {
    data: { user },
    error: authError,
  } = await supabase.auth.getUser();
  if (authError) throw authError;
  if (!user) throw new Error('Not authenticated');

  const payload = {
    user_id: user.id,
    full_name: input.fullName.trim(),
    timezone: input.timezone || 'UTC',
    persona: input.persona?.trim() || null,
    bio: input.bio?.trim() || null,
    avatar_url: input.avatarUrl?.trim() || null,
    updated_at: new Date().toISOString(),
  };

  const { error } = await supabase.from('profiles').upsert(payload, { onConflict: 'user_id' });
  if (error) throw error;
};

export const fetchProfileSummaries = async (
  userIds: string[],
): Promise<Record<string, ProfileSummary>> => {
  if (userIds.length === 0) return {};
  const uniqueIds = Array.from(new Set(userIds));
  const { data, error } = await supabase.rpc('get_profile_labels', {
    target_ids: uniqueIds,
  });
  if (error) throw error;
  const summaries: Record<string, ProfileSummary> = {};
  (data ?? []).forEach((row: any) => {
    summaries[row.user_id] = {
      fullName: row.full_name ?? null,
      persona: row.persona ?? null,
      avatarUrl: row.avatar_url ?? null,
    };
  });
  return summaries;
};

export const fetchUserTimezone = async (userId: string): Promise<Result<string | null>> => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('timezone')
      .eq('user_id', userId)
      .maybeSingle();
    if (error) throw error;
    return ok(data?.timezone ?? null);
  } catch (error) {
    return err(error);
  }
};
